import { useEffect, useRef, useState } from 'react';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';
import { getOpportunities } from '@/services/mockData';

const STAGES = [
  { key: 'link',     label: '1 · Link',     bg: 'var(--paper-2)',     border: 'var(--hairline)' },
  { key: 'qualify',  label: '2 · Qualify',  bg: 'var(--warn-soft)',   border: 'var(--warn)'     },
  { key: 'proposal', label: '3 · Proposal', bg: 'var(--accent-soft)', border: 'var(--accent)'   },
  { key: 'deferred', label: '4 · Deferred', bg: 'var(--paper-2)',     border: 'var(--ink-3)'    },
  { key: 'lost',     label: '5 · Lost',     bg: 'var(--danger-soft)', border: 'var(--danger)'   },
  { key: 'sold',     label: '6 · Sold',     bg: 'var(--pos-soft)',    border: 'var(--pos)'      },
];

const MONO_LABEL = {
  fontFamily: 'var(--mono)', fontSize: 8, textTransform: 'uppercase',
  letterSpacing: '0.06em', color: 'var(--ink-4)', marginBottom: 2,
};

const INPUT = {
  fontFamily: 'var(--sans)', fontSize: 11, color: 'var(--ink)',
  background: 'transparent', border: '1px solid var(--hairline)',
  padding: '4px 7px', width: '100%', outline: 'none', boxSizing: 'border-box', display: 'block',
};

function blankOpp() {
  return {
    id: `opp_${Date.now()}`,
    stage: 'link',
    firm: '',
    firm_url: '',
    viable: 'TBC',
    connect: '',
    connect_url: '',
    position: '',
    connect_level: 'L1',
    route_to_reach: '',
    route_url: '',
    route_level: 'L1',
    history: [],
    commentary: '',
  };
}

// ── Chips ────────────────────────────────────────────────────────────────────

function ViableChip({ viable }) {
  if (viable === 'Yes')      return <span className="chip" style={{ background: 'var(--pos-soft)', borderColor: 'var(--pos)', color: 'var(--accent-ink)', fontSize: 9, padding: '1px 5px' }}>YES</span>;
  if (viable === 'Possible') return <span className="chip" style={{ background: 'var(--warn-soft)', borderColor: 'var(--warn)', fontSize: 9, padding: '1px 5px' }}>POSSIBLE</span>;
  return <span className="chip muted" style={{ fontSize: 9, padding: '1px 5px' }}>TBC</span>;
}

function LevelChip({ level }) {
  return (
    <span className="chip muted" style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '1px 5px', letterSpacing: '0.04em' }}>
      {level}
    </span>
  );
}

function DueTag({ status }) {
  if (status === 'overdue') {
    return (
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase',
        background: 'var(--warn-soft)', border: '1px solid var(--warn)',
        padding: '0px 4px', color: 'var(--ink)', flexShrink: 0,
      }}>! OVERDUE</span>
    );
  }
  if (status === 'due-soon') {
    return (
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.06em', textTransform: 'uppercase',
        background: 'var(--accent-soft)', border: '1px solid var(--accent)',
        padding: '0px 4px', color: 'var(--ink)', flexShrink: 0,
      }}>◷ DUE SOON</span>
    );
  }
  return null;
}

// Link that stops propagation so it doesn't trigger the card's onClick drill-in
function ExtLink({ href, children, style }) {
  if (!href) return <span style={style}>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ color: 'inherit', textDecoration: 'none', ...style }}
    >
      {children}<span style={{ marginLeft: 2, fontSize: 7, opacity: 0.45 }}>↗</span>
    </a>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr || dateStr === 'tbc') return 'TBC';
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function dueStatus(dateStr) {
  if (!dateStr || dateStr === 'tbc') return 'ok';
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const days = (due - now) / 86400000;
  if (days < 0) return 'overdue';
  if (days <= 7) return 'due-soon';
  return 'ok';
}

// ── Segmented control ─────────────────────────────────────────────────────────

function SegmentedSelect({ value, options, onChange }) {
  return (
    <div style={{ display: 'inline-flex', gap: 3 }}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          style={{
            fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.04em',
            textTransform: 'uppercase', padding: '2px 8px', cursor: 'pointer',
            border: `1px solid ${value === opt ? 'var(--ink)' : 'var(--hairline)'}`,
            background: value === opt ? 'var(--ink)' : 'transparent',
            color: value === opt ? 'var(--paper)' : 'var(--ink-3)',
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// URL input row with inline open-link button
function UrlField({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 7 }}>
      <label style={{ ...MONO_LABEL, display: 'block' }}>{label}</label>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="url"
          value={value}
          onChange={onChange}
          placeholder="https://www.linkedin.com/…"
          style={INPUT}
        />
        {value && (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)',
              border: '1px solid var(--hairline)', padding: '0 8px',
              textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center',
            }}
          >
            ↗
          </a>
        )}
      </div>
    </div>
  );
}

// ── Drill-in ──────────────────────────────────────────────────────────────────

const MOVE_BTN = {
  fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--ink-3)', background: 'transparent', border: '1px solid var(--ink-3)',
  padding: '3px 9px', cursor: 'pointer',
};

export function OppDrillIn({ opp, isNew = false, onBack, onChange, onDelete, onConvert, onMoveToContact, onMoveToNurture, showNav = true }) {
  const set = (field) => (e) => onChange({ [field]: e.target.value });

  const addStep = () => onChange({ history: [{ date: today(), note: '' }, ...opp.history] });

  const updateStep = (i, patch) =>
    onChange({ history: opp.history.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) });

  const deleteStep = (i) =>
    onChange({ history: opp.history.filter((_, idx) => idx !== i) });

  return (
    <div style={{ gridColumn: '1 / -1' }}>

      {/* Nav row */}
      {showNav && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 14 }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--ink-3)', background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 0, display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            BOARD →
          </button>
        </div>
      )}

      {/* Identity */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ marginBottom: 10 }}>
          <label style={{ ...MONO_LABEL, display: 'block' }}>Firm</label>
          <input
            style={{ ...INPUT, fontSize: 18, fontFamily: 'var(--serif)', padding: '4px 7px' }}
            value={opp.firm}
            onChange={set('firm')}
            placeholder="Company name…"
          />
        </div>
        <UrlField label="Company LinkedIn" value={opp.firm_url} onChange={set('firm_url')} />
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <label style={{ ...MONO_LABEL, display: 'block', marginBottom: 4 }}>Stage</label>
            <select
              value={opp.stage}
              onChange={set('stage')}
              style={{ ...INPUT, width: 'auto', padding: '3px 6px', cursor: 'pointer' }}
            >
              {STAGES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ ...MONO_LABEL, display: 'block', marginBottom: 4 }}>Viable</label>
            <SegmentedSelect
              value={opp.viable}
              options={['Yes', 'Possible', 'TBC']}
              onChange={(v) => onChange({ viable: v })}
            />
          </div>
        </div>
      </div>

      {/* LinkedIn Connect + Route */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div>
            <SectionTitle label="LinkedIn Connect" />
            <div style={{ marginBottom: 7 }}>
              <label style={{ ...MONO_LABEL, display: 'block' }}>Name</label>
              <input style={INPUT} value={opp.connect} onChange={set('connect')} placeholder="Full name…" />
            </div>
            <UrlField label="LinkedIn URL" value={opp.connect_url} onChange={set('connect_url')} />
            <div style={{ marginBottom: 7 }}>
              <label style={{ ...MONO_LABEL, display: 'block' }}>Position</label>
              <input style={INPUT} value={opp.position} onChange={set('position')} placeholder="Job title…" />
            </div>
            <div>
              <label style={{ ...MONO_LABEL, display: 'block', marginBottom: 4 }}>Connect Level</label>
              <SegmentedSelect
                value={opp.connect_level}
                options={['L1', 'L2', 'L3']}
                onChange={(v) => onChange({ connect_level: v })}
              />
            </div>
          </div>
          <div>
            <SectionTitle label="Route to Reach" />
            <div style={{ marginBottom: 7 }}>
              <label style={{ ...MONO_LABEL, display: 'block' }}>Via</label>
              <input style={INPUT} value={opp.route_to_reach} onChange={set('route_to_reach')} placeholder="Full name…" />
            </div>
            <UrlField label="LinkedIn URL" value={opp.route_url} onChange={set('route_url')} />
            <div>
              <label style={{ ...MONO_LABEL, display: 'block', marginBottom: 4 }}>Route Level</label>
              <SegmentedSelect
                value={opp.route_level}
                options={['L1', 'L2', 'L3']}
                onChange={(v) => onChange({ route_level: v })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <SectionTitle label="Next Steps" meta={`${opp.history.length} ENTRIES`} />
          <button
            type="button"
            onClick={addStep}
            style={{
              fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--accent)', background: 'transparent', border: '1px solid var(--accent)',
              padding: '3px 9px', cursor: 'pointer',
            }}
          >
            + ADD STEP
          </button>
        </div>
        {opp.history.length === 0 && (
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
            NO STEPS YET — ADD ONE ABOVE
          </div>
        )}
        <div style={{ display: 'grid', gap: 6 }}>
          {opp.history.map((h, i) => (
            <div
              key={i}
              style={{
                display: 'grid', gridTemplateColumns: '140px 1fr 22px', gap: 6, alignItems: 'center',
                paddingBottom: i < opp.history.length - 1 ? 6 : 0,
                borderBottom: i < opp.history.length - 1 ? '1px dashed var(--hairline)' : 'none',
              }}
            >
              <input
                type="date"
                value={h.date === 'tbc' ? '' : h.date}
                onChange={(e) => updateStep(i, { date: e.target.value || 'tbc' })}
                style={{ ...INPUT, fontFamily: 'var(--mono)', fontSize: 10 }}
              />
              <input
                value={h.note}
                onChange={(e) => updateStep(i, { note: e.target.value })}
                style={{ ...INPUT, ...(i === 0 ? { fontWeight: 500 } : { color: 'var(--ink-3)' }) }}
                placeholder="Next step note…"
              />
              <button
                type="button"
                onClick={() => deleteStep(i)}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--ink-4)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Commentary */}
      <div className="card" style={{ marginBottom: showNav ? 10 : 0 }}>
        <SectionTitle label="Commentary" />
        <textarea
          value={opp.commentary}
          onChange={set('commentary')}
          placeholder="Add commentary…"
          style={{ ...INPUT, minHeight: 80, resize: 'vertical', lineHeight: 1.5 }}
        />
      </div>

      {/* Transition Opportunity */}
      {showNav && (
        <div className="card">
          <SectionTitle label="Transition Opportunity" />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            {/* Left — transition actions (hidden for brand-new unsaved opps) */}
            {!isNew && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <button
                  type="button"
                  onClick={onDelete}
                  style={{
                    fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: 'var(--danger)', background: 'transparent', border: '1px solid var(--danger)',
                    padding: '3px 9px', cursor: 'pointer',
                  }}
                >
                  DELETE
                </button>
                {onMoveToContact && <button type="button" onClick={() => onMoveToContact(opp)} style={MOVE_BTN}>→ ACTIVE CONTACT</button>}
                {onMoveToNurture && <button type="button" onClick={() => onMoveToNurture(opp)} style={MOVE_BTN}>→ NURTURE CONTACT</button>}
                {opp.stage !== 'lost' && onConvert && (
                  <button
                    type="button"
                    onClick={() => onConvert(opp)}
                    style={{
                      fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
                      color: 'var(--paper)', background: 'var(--pos)', border: '1px solid var(--pos)',
                      padding: '3px 9px', cursor: 'pointer',
                    }}
                  >
                    START ENGAGEMENT →
                  </button>
                )}
              </div>
            )}
            {/* Right — commit / abandon */}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button
                type="button"
                onClick={onBack}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
                  background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)',
                  padding: '6px 12px', cursor: 'pointer',
                }}
              >
                {isNew ? 'Add' : 'Save'}
              </button>
              <button
                type="button"
                onClick={isNew ? onDelete : onBack}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
                  background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--hairline)',
                  padding: '6px 12px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Board card ────────────────────────────────────────────────────────────────

function OppCard({ opp, onClick, onDragStart, onDragEnd, isDimmed }) {
  const current = opp.history?.[0];
  const wasDragged = useRef(false);
  const status = current ? dueStatus(current.date) : 'ok';

  const cardShadow = status === 'overdue'  ? 'inset 3px 0 0 0 var(--warn)'
                   : status === 'due-soon' ? 'inset 3px 0 0 0 var(--accent)'
                   : 'none';
  const dateColor  = status === 'overdue'  ? 'var(--warn)'
                   : status === 'due-soon' ? 'var(--accent)'
                   : 'var(--ink-4)';
  const nextBorder = status === 'overdue'  ? '1px dashed var(--warn)'
                   : status === 'due-soon' ? '1px dashed var(--accent)'
                   : '1px dashed var(--hairline)';

  return (
    <div
      draggable
      onDragStart={() => { wasDragged.current = true; onDragStart(); }}
      onDragEnd={() => { onDragEnd(); setTimeout(() => { wasDragged.current = false; }, 0); }}
      onClick={() => { if (!wasDragged.current) onClick(); }}
      style={{
        border: '1px solid var(--hairline)', padding: '7px 9px', background: 'var(--paper)',
        cursor: 'grab', opacity: isDimmed ? 0.35 : 1, transition: 'opacity 0.12s',
        boxShadow: cardShadow,
      }}
    >
      {/* Viable + connect level */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <ViableChip viable={opp.viable} />
        <LevelChip level={opp.connect_level} />
      </div>

      {/* Firm — clickable if URL exists */}
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.2, marginBottom: 6 }}>
        <ExtLink href={opp.firm_url}>{opp.firm || <span style={{ color: 'var(--ink-4)', fontWeight: 400, fontStyle: 'italic' }}>Untitled</span>}</ExtLink>
      </div>

      {/* Connect */}
      <div style={{ marginBottom: 5 }}>
        <div style={MONO_LABEL}>Connect</div>
        <div style={{ fontSize: 10, color: 'var(--ink-2)', lineHeight: 1.3 }}>
          <ExtLink href={opp.connect_url}>{opp.connect || '—'}</ExtLink>
        </div>
        <div style={{ fontSize: 9, color: 'var(--ink-4)', lineHeight: 1.3 }}>{opp.position}</div>
      </div>

      {/* Route */}
      {opp.route_to_reach && (
        <div style={{ marginBottom: 5 }}>
          <div style={{ ...MONO_LABEL, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Route</span>
            <LevelChip level={opp.route_level} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--ink-2)', lineHeight: 1.3 }}>
            <ExtLink href={opp.route_url}>{opp.route_to_reach}</ExtLink>
          </div>
        </div>
      )}

      {/* Current next step only */}
      {current && (
        <div style={{ borderTop: nextBorder, paddingTop: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={MONO_LABEL}>Next</div>
            <DueTag status={status} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '26px 1fr', gap: 4 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 8, color: dateColor, paddingTop: 1 }}>
              {fmtDate(current.date)}
            </span>
            <span style={{ fontSize: 9, color: 'var(--ink-2)', lineHeight: 1.3 }}>{current.note}</span>
          </div>
        </div>
      )}

      <div style={{ marginTop: 6, textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 8, color: 'var(--ink-4)', letterSpacing: '0.04em' }}>
        VIEW ›
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function OpportunitiesDetail({ onClose, onPromoteToClient, initialOpps = [], onMoveToContact, onMoveToNurture }) {
  const [opps, setOpps] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [isNewOpp, setIsNewOpp] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const initialRef = useRef(initialOpps);

  useEffect(() => {
    let cancelled = false;
    getOpportunities().then((d) => { if (!cancelled) setOpps([...d, ...initialRef.current]); });
    return () => { cancelled = true; };
  }, []);

  const updateOpp = (id, patch) =>
    setOpps((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));

  const handleDrop = (stageKey) => {
    if (draggingId) updateOpp(draggingId, { stage: stageKey });
    setDraggingId(null);
    setDragOverStage(null);
  };

  const deleteOpp = (id) => {
    setOpps((prev) => prev.filter((o) => o.id !== id));
    setSelectedId(null);
    setIsNewOpp(false);
  };

  const addOpportunity = () => {
    const opp = blankOpp();
    setOpps((prev) => [opp, ...prev]);
    setSelectedId(opp.id);
    setIsNewOpp(true);
  };

  const selectedOpp = selectedId ? opps.find((o) => o.id === selectedId) : null;

  const byStage = Object.fromEntries(STAGES.map((s) => [s.key, opps.filter((o) => o.stage === s.key)]));
  const total = opps.length;
  const sold  = byStage.sold?.length ?? 0;

  return (
    <DetailShell eyebrow="02 · Pipeline Builder" title="Active Opportunities" onClose={onClose}>

      {selectedOpp ? (
        <OppDrillIn
          opp={selectedOpp}
          isNew={isNewOpp}
          onBack={() => { setSelectedId(null); setIsNewOpp(false); }}
          onChange={(patch) => updateOpp(selectedOpp.id, patch)}
          onDelete={() => deleteOpp(selectedOpp.id)}
          onConvert={onPromoteToClient ? (opp) => { deleteOpp(opp.id); onPromoteToClient(opp); } : null}
          onMoveToContact={onMoveToContact ? (opp) => { deleteOpp(opp.id); onMoveToContact(opp); } : null}
          onMoveToNurture={onMoveToNurture ? (opp) => { deleteOpp(opp.id); onMoveToNurture(opp); } : null}
        />
      ) : (
        <>
          {/* Overview */}
          <div className="card">
            <SectionTitle label="Pipeline overview" meta={`${total} ACTIVE · ${sold} SOLD`} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
              {STAGES.map((s) => (
                <div key={s.key} style={{ border: `1px solid ${s.border}`, padding: '8px 10px', background: s.bg }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 26, lineHeight: 1 }}>{byStage[s.key]?.length ?? 0}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', marginTop: 2 }}>OPPORTUNITIES</div>
                </div>
              ))}
            </div>
          </div>

          {/* Board */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <SectionTitle label="Opportunity board" meta={`BY STAGE · ${total} TOTAL`} />
              <button
                type="button"
                onClick={addOpportunity}
                style={{
                  fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: 'var(--accent)', background: 'transparent', border: '1px solid var(--accent)',
                  padding: '3px 10px', cursor: 'pointer',
                }}
              >
                + NEW OPPORTUNITY
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, alignItems: 'start' }}>
              {STAGES.map((s) => {
                const isOver = dragOverStage === s.key && draggingId !== null;
                return (
                  <div
                    key={s.key}
                    onDragOver={(e) => { e.preventDefault(); setDragOverStage(s.key); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStage(null); }}
                    onDrop={() => handleDrop(s.key)}
                  >
                    <div style={{
                      fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
                      padding: '4px 8px', background: s.bg,
                      border: isOver ? `2px solid ${s.border}` : `1px solid ${s.border}`,
                      marginBottom: 6, display: 'flex', justifyContent: 'space-between',
                    }}>
                      <span>{s.label}</span>
                      <span style={{ color: 'var(--ink-3)' }}>{byStage[s.key]?.length ?? 0}</span>
                    </div>
                    <div style={{
                      display: 'grid', gap: 6,
                      minHeight: isOver ? 48 : 0,
                      outline: isOver ? `1px dashed ${s.border}` : 'none',
                      outlineOffset: 2,
                    }}>
                      {(byStage[s.key] ?? []).map((opp) => (
                        <OppCard
                          key={opp.id}
                          opp={opp}
                          onClick={() => setSelectedId(opp.id)}
                          onDragStart={() => setDraggingId(opp.id)}
                          onDragEnd={() => { setDraggingId(null); setDragOverStage(null); }}
                          isDimmed={draggingId !== null && draggingId !== opp.id}
                        />
                      ))}
                      {(byStage[s.key]?.length ?? 0) === 0 && !isOver && (
                        <div style={{ border: '1px dashed var(--hairline)', padding: '16px 9px', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', letterSpacing: '0.06em' }}>
                          NONE
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

    </DetailShell>
  );
}

export default OpportunitiesDetail;
